import { baseApi } from '@/store/baseApi';

import type {
  BulkCreateServiceTypesPayload,
  BulkCreateServiceTypesResponse,
  CreateServiceGroupPayload,
  CreateServiceGroupResponse,
  CreateServiceTypePayload,
  CreateServiceTypeResponse,
  DeleteServiceTypeParams,
  DeleteServiceTypeResponse,
  GetServiceGroupsParams,
  GetServiceGroupsResponse,
  GetServiceTypesParams,
  GetServiceTypesResponse,
  UnarchiveServiceTypeResponse,
  UpdateServiceGroupPayload,
  UpdateServiceGroupResponse,
  UpdateServiceTypePayload,
  UpdateServiceTypeResponse,
} from '@/features/service-types/types';
import type { APIResponse } from '@web/types';

export const serviceTypesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getServiceTypes: builder.query<
      APIResponse<GetServiceTypesResponse>,
      GetServiceTypesParams | void
    >({
      query: (params) => ({
        url: 'service-types',
        method: 'GET',
        params: params ?? undefined,
      }),
      providesTags: [{ type: 'ServiceTypes', id: 'LIST' }],
    }),
    createServiceType: builder.mutation<
      APIResponse<CreateServiceTypeResponse>,
      CreateServiceTypePayload
    >({
      query: (body) => ({
        url: 'service-types',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'ServiceTypes', id: 'LIST' }],
    }),
    createServiceTypesBulk: builder.mutation<
      APIResponse<BulkCreateServiceTypesResponse>,
      BulkCreateServiceTypesPayload
    >({
      query: (body) => ({
        url: 'service-types/bulk-create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'ServiceTypes', id: 'LIST' }],
    }),
    updateServiceType: builder.mutation<
      APIResponse<UpdateServiceTypeResponse>,
      { serviceTypeId: string; payload: UpdateServiceTypePayload }
    >({
      query: ({ serviceTypeId, payload }) => ({
        url: `service-types/${serviceTypeId}`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: [{ type: 'ServiceTypes', id: 'LIST' }],
    }),
    deleteServiceType: builder.mutation<
      APIResponse<DeleteServiceTypeResponse>,
      { serviceTypeId: string; params?: DeleteServiceTypeParams }
    >({
      query: ({ serviceTypeId, params }) => ({
        url: `service-types/${serviceTypeId}`,
        method: 'DELETE',
        params: params ?? undefined,
      }),
      invalidatesTags: [{ type: 'ServiceTypes', id: 'LIST' }],
    }),
    unarchiveServiceType: builder.mutation<
      APIResponse<UnarchiveServiceTypeResponse>,
      { serviceTypeId: string }
    >({
      query: ({ serviceTypeId }) => ({
        url: `service-types/${serviceTypeId}/unarchive`,
        method: 'PATCH',
      }),
      invalidatesTags: [{ type: 'ServiceTypes', id: 'LIST' }],
    }),
    getServiceGroups: builder.query<
      APIResponse<GetServiceGroupsResponse>,
      GetServiceGroupsParams | void
    >({
      query: (params) => ({
        url: 'service-types/service-groups',
        method: 'GET',
        params: params ?? undefined,
      }),
      providesTags: [{ type: 'ServiceGroups', id: 'LIST' }],
    }),
    createServiceGroup: builder.mutation<
      APIResponse<CreateServiceGroupResponse>,
      CreateServiceGroupPayload
    >({
      query: (body) => ({
        url: 'service-types/service-groups',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'ServiceGroups', id: 'LIST' }],
    }),
    updateServiceGroup: builder.mutation<
      APIResponse<UpdateServiceGroupResponse>,
      { serviceGroupId: string; payload: UpdateServiceGroupPayload }
    >({
      query: ({ serviceGroupId, payload }) => ({
        url: `service-types/service-groups/${serviceGroupId}`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: [{ type: 'ServiceGroups', id: 'LIST' }],
    }),
    deleteServiceGroup: builder.mutation<
      APIResponse<null>,
      { serviceGroupId: string }
    >({
      query: ({ serviceGroupId }) => ({
        url: `service-types/service-groups/${serviceGroupId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'ServiceGroups', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetServiceTypesQuery,
  useCreateServiceTypeMutation,
  useCreateServiceTypesBulkMutation,
  useUpdateServiceTypeMutation,
  useDeleteServiceTypeMutation,
  useUnarchiveServiceTypeMutation,
  useGetServiceGroupsQuery,
  useCreateServiceGroupMutation,
  useUpdateServiceGroupMutation,
  useDeleteServiceGroupMutation,
} = serviceTypesApi;
